// © 2026 Buddy Media LLC. All rights reserved.
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { AdminCard } from "@/components/AdminCard";
import { AdminPageShell } from "@/components/AdminPageShell";
import { ApiError, mcpFetch } from "@/lib/api";

// T-073 — Q stream orchestration event log.
//
// Reverse-chronological feed of every orchestration decision +
// worker acknowledgement + completion across the cluster. Distinct
// from per-task progress_log (internal narrative) and admin_audit_log
// (admin actions on backend resources).
//
// Backend proxy at GET /api/admin/q-stream/events forwards to MCP's
// /tools/q-stream. v1 is admin-only + manual refresh; polling /
// realtime push is a v2 follow-up.

type QStreamEventType =
  | "decision_dispatched"
  | "worker_received"
  | "worker_completion"
  | "mid_task"
  | "worker_warn"
  | "worker_error"
  | "worker_spawn"
  | "worker_token_usage"
  | "worker_spawn_done"
  | "heartbeat"
  | "wake_up"
  | "worker_heal_attempt"
  | "worker_heal_success"
  | "worker_heal_failed"
  | "worker_heal_escalated";

type QStreamEvent = {
  id: string;
  ts: string;
  event_type: QStreamEventType;
  actor: string;
  task_id: string | null;
  message: string;
  metadata: Record<string, unknown>;
};

// Filter chip catalog (T-129 Phase 4B). Each chip has a default
// state — heartbeat is OFF (too noisy); the rest default ON.
type ChipDef = {
  key: QStreamEventType;
  label: string;
  defaultOn: boolean;
  color: string;
};

const CHIP_DEFS: ChipDef[] = [
  { key: "decision_dispatched", label: "Dispatched", defaultOn: true, color: "border-sky-500/40 bg-sky-500/15 text-sky-300" },
  { key: "worker_received", label: "Received", defaultOn: true, color: "border-amber-500/40 bg-amber-500/15 text-amber-300" },
  { key: "worker_spawn", label: "Spawn", defaultOn: true, color: "border-violet-500/40 bg-violet-500/15 text-violet-300" },
  { key: "worker_token_usage", label: "Token usage", defaultOn: true, color: "border-fuchsia-500/40 bg-fuchsia-500/15 text-fuchsia-300" },
  { key: "worker_spawn_done", label: "Spawn done", defaultOn: true, color: "border-cyan-500/40 bg-cyan-500/15 text-cyan-300" },
  { key: "worker_completion", label: "Completed", defaultOn: true, color: "border-emerald-500/40 bg-emerald-500/15 text-emerald-300" },
  { key: "mid_task", label: "Mid-task", defaultOn: true, color: "border-slate-600 bg-slate-800 text-slate-300" },
  { key: "worker_warn", label: "Warn", defaultOn: true, color: "border-amber-600/40 bg-amber-600/15 text-amber-200" },
  { key: "worker_error", label: "Error", defaultOn: true, color: "border-rose-500/40 bg-rose-500/15 text-rose-300" },
  { key: "heartbeat", label: "Heartbeat", defaultOn: false, color: "border-slate-700 bg-slate-900 text-slate-400" },
];

const EVENT_TYPE_PILLS: Record<string, string> = Object.fromEntries(
  CHIP_DEFS.map((c) => [c.key, c.color]),
);

const EVENT_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  CHIP_DEFS.map((c) => [c.key, c.label]),
);

const FILTER_CHIPS_LS_KEY = "qstream:enabled-event-types:v1";
const LIVE_TAIL_LS_KEY = "qstream:live-tail:v1";
const LIVE_TAIL_POLL_MS = 5_000;

function loadEnabledChips(): Set<string> {
  try {
    const raw = localStorage.getItem(FILTER_CHIPS_LS_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) return new Set(arr.map(String));
    }
  } catch {
    // fall through to defaults
  }
  return new Set(CHIP_DEFS.filter((c) => c.defaultOn).map((c) => c.key));
}

function saveEnabledChips(set: Set<string>): void {
  try {
    localStorage.setItem(FILTER_CHIPS_LS_KEY, JSON.stringify(Array.from(set)));
  } catch {
    // ignore quota errors
  }
}

function loadLiveTail(): boolean {
  try {
    return localStorage.getItem(LIVE_TAIL_LS_KEY) === "1";
  } catch {
    return false;
  }
}

function saveLiveTail(on: boolean): void {
  try {
    localStorage.setItem(LIVE_TAIL_LS_KEY, on ? "1" : "0");
  } catch {
    // ignore
  }
}

function eventTypePill(t: QStreamEvent["event_type"]): { label: string; color: string } {
  const color = EVENT_TYPE_PILLS[t];
  const label = EVENT_TYPE_LABELS[t] ?? t;
  return color
    ? { label, color }
    : { label, color: "border-slate-700 bg-slate-900 text-slate-400" };
}

function fmtTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function describeError(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const detail = (err.data as { detail?: string } | undefined)?.detail;
    return `${fallback} (HTTP ${err.status})${detail ? `: ${detail}` : ""}`;
  }
  return fallback;
}

export function QStreamPage() {
  const [searchParams] = useSearchParams();
  const initialActor = searchParams.get("actor") ?? "";

  const [events, setEvents] = useState<QStreamEvent[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Single-event-type select kept for narrow drilldowns (e.g. only
  // worker_token_usage). The chip multi-select below is the new
  // primary filter — they compose: server-side single-type filter
  // first, then client-side chip mask on top.
  const [filterEventType, setFilterEventType] = useState<string>("");
  const [filterActor, setFilterActor] = useState<string>(initialActor);
  const [filterTaskId, setFilterTaskId] = useState<string>("");
  const [windowChoice, setWindowChoice] = useState<"24h" | "7d" | "30d" | "all">("24h");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [enabledChips, setEnabledChips] = useState<Set<string>>(loadEnabledChips);
  const [liveTail, setLiveTail] = useState<boolean>(loadLiveTail);
  const tableRef = useRef<HTMLDivElement | null>(null);

  // Prefill actor from ?actor=... when the dashboard deep-links in.
  useEffect(() => {
    if (initialActor && !filterActor) setFilterActor(initialActor);
    // intentionally only on mount-time URL state; subsequent edits
    // are user-driven and shouldn't be clobbered
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    params.set("limit", "200");
    if (filterEventType) params.set("event_type", filterEventType);
    if (filterActor.trim()) params.set("actor", filterActor.trim());
    if (filterTaskId.trim()) params.set("task_id", filterTaskId.trim());
    if (windowChoice !== "all") {
      const hours = windowChoice === "24h" ? 24 : windowChoice === "7d" ? 24 * 7 : 24 * 30;
      const since = new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
      params.set("since", since);
    }

    mcpFetch<QStreamEvent[]>(`/admin/api/q-stream/events?${params.toString()}`)
      .then((data) => {
        if (!cancelled) setEvents(data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(describeError(err, "Failed to load Q stream"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey, filterEventType, filterActor, filterTaskId, windowChoice]);

  // Live-tail auto-refresh: when ON, bump refreshKey every 5sec so
  // the existing fetch effect re-runs. When OFF, no polling — Marc
  // controls cadence via the explicit Refresh button.
  useEffect(() => {
    if (!liveTail) return;
    const id = setInterval(() => setRefreshKey((k) => k + 1), LIVE_TAIL_POLL_MS);
    return () => clearInterval(id);
  }, [liveTail]);

  // After each fetch, when live-tail is ON, scroll the table back
  // to top (newest event — list is reverse-chronological, newest at
  // top). When OFF, preserve the user's scroll position.
  useEffect(() => {
    if (liveTail && events && tableRef.current) {
      tableRef.current.scrollTop = 0;
    }
  }, [events, liveTail]);

  const actorChoices = useMemo(() => {
    if (!events) return [] as string[];
    const set = new Set<string>();
    for (const e of events) {
      if (!RETIRED_ACTORS.has(e.actor)) set.add(e.actor);
    }
    return Array.from(set).sort();
  }, [events]);

  // Compute previous worker_token_usage output_tokens per task within
  // the visible window, so the per-event render can show "+<delta>"
  // against the prior emit. Walk events in reverse-chrono (oldest
  // first) so each token-usage event sees its predecessor.
  const tokenDeltaById = useMemo(() => {
    const out = new Map<string, number>();
    if (!events) return out;
    const lastByTask = new Map<string, number>();
    const chrono = [...events].reverse();
    for (const e of chrono) {
      if (e.event_type !== "worker_token_usage" || !e.task_id) continue;
      const meta = e.metadata as { output_tokens?: number };
      const cur = typeof meta.output_tokens === "number" ? meta.output_tokens : null;
      if (cur == null) continue;
      const prev = lastByTask.get(e.task_id);
      if (prev != null) out.set(e.id, cur - prev);
      lastByTask.set(e.task_id, cur);
    }
    return out;
  }, [events]);

  // Apply chip filter + retired-actor filter client-side.
  const visibleEvents = useMemo(() => {
    if (!events) return null;
    return events.filter(
      (e) => enabledChips.has(e.event_type) && !RETIRED_ACTORS.has(e.actor),
    );
  }, [events, enabledChips]);

  const toggleChip = (key: string) => {
    setEnabledChips((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      saveEnabledChips(next);
      return next;
    });
  };

  const toggleLiveTail = () => {
    setLiveTail((prev) => {
      const next = !prev;
      saveLiveTail(next);
      return next;
    });
  };

  return (
    <AdminPageShell
      title="Q stream"
      description="Chronological log of every orchestration event — task creation (dispatched), worker claim (received), terminal completion (completed), plus mid-task posts (blockers, surface requests, design pivots). Distinct from per-task progress_log and admin_audit_log; this is the cross-task system timeline. v1 is manual refresh; realtime is a follow-up."
    >
      <div className="space-y-4">
        <ClusterPulseSection />

        {error ? (
          <AdminCard className="border-rose-900/40 bg-rose-950/20">
            <p className="text-sm text-rose-200">{error}</p>
          </AdminCard>
        ) : null}

        <AdminCard className="space-y-3">
          <div className="flex flex-wrap gap-3">
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Event type
              </label>
              <select
                value={filterEventType}
                onChange={(e) => setFilterEventType(e.target.value)}
                className="mt-1 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-white"
              >
                <option value="">All</option>
                {CHIP_DEFS.map((et) => (
                  <option key={et.key} value={et.key}>
                    {et.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Actor
              </label>
              <input
                list="qstream-actor-choices"
                value={filterActor}
                onChange={(e) => setFilterActor(e.target.value)}
                placeholder="frontend-worker / backend-worker / wire-test-worker / marc"
                className="mt-1 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-white"
              />
              <datalist id="qstream-actor-choices">
                {actorChoices.map((a) => (
                  <option key={a} value={a} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Task ID
              </label>
              <input
                value={filterTaskId}
                onChange={(e) => setFilterTaskId(e.target.value)}
                placeholder="T-073"
                className="mt-1 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-white"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                Window
              </label>
              <select
                value={windowChoice}
                onChange={(e) => setWindowChoice(e.target.value as typeof windowChoice)}
                className="mt-1 rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-white"
              >
                <option value="24h">Last 24h</option>
                <option value="7d">Last 7 days</option>
                <option value="30d">Last 30 days</option>
                <option value="all">All time</option>
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button
                type="button"
                onClick={() => setRefreshKey((k) => k + 1)}
                disabled={loading}
                className="rounded-md bg-pink-500 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
              >
                {loading ? "Loading…" : "Refresh"}
              </button>
              <button
                type="button"
                onClick={toggleLiveTail}
                aria-pressed={liveTail}
                className={`rounded-md border px-3 py-1.5 text-xs font-medium transition ${
                  liveTail
                    ? "border-emerald-500 bg-emerald-500/15 text-emerald-200"
                    : "border-slate-700 bg-slate-900 text-slate-300 hover:border-emerald-500/60"
                }`}
                title={
                  liveTail
                    ? "Live-tail ON — auto-refresh every 5s + scroll to newest"
                    : "Live-tail OFF — manual refresh; scroll preserved"
                }
              >
                {liveTail ? "🟢 Live-tail ON" : "Live-tail OFF"}
              </button>
            </div>
          </div>

          {/* T-129 — per-event-type filter chips. Multi-select; client-
              side filter on already-fetched events; persisted to LS. */}
          <div>
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Event types ({enabledChips.size}/{CHIP_DEFS.length} on)
            </div>
            <div className="flex flex-wrap gap-1.5">
              {CHIP_DEFS.map((chip) => {
                const on = enabledChips.has(chip.key);
                return (
                  <button
                    key={chip.key}
                    type="button"
                    onClick={() => toggleChip(chip.key)}
                    aria-pressed={on}
                    className={`rounded-md border px-2 py-0.5 text-[11px] font-mono transition ${
                      on
                        ? chip.color
                        : "border-slate-800 bg-slate-950 text-slate-600 line-through"
                    }`}
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>
          </div>

          <p className="text-[11px] text-slate-500">
            {events === null
              ? "Loading…"
              : `${visibleEvents?.length ?? 0} of ${events.length} events shown${events.length === 200 ? " (limit reached — narrow filters for older)" : ""}`}
          </p>
        </AdminCard>

        {visibleEvents && visibleEvents.length === 0 ? (
          <AdminCard>
            <p className="text-sm text-slate-400">
              {events && events.length > 0
                ? "No events match the chip filter — toggle chips above."
                : "No events match."}
            </p>
          </AdminCard>
        ) : null}

        <div
          ref={tableRef}
          className="max-h-[70vh] divide-y divide-slate-800/60 overflow-y-auto rounded-xl border border-slate-800 bg-slate-950/60"
        >
          {(visibleEvents ?? []).map((e) => {
            const pill = eventTypePill(e.event_type);
            const isExpanded = expanded.has(e.id);
            const hasMetadata = Object.keys(e.metadata).length > 0;
            const isTokenUsage = e.event_type === "worker_token_usage";
            const tu = isTokenUsage
              ? (e.metadata as { input_tokens?: number; output_tokens?: number })
              : null;
            const delta = tokenDeltaById.get(e.id);
            const bodyText =
              isTokenUsage && tu
                ? `in=${tu.input_tokens ?? "?"} · out=${tu.output_tokens ?? "?"}${
                    delta !== undefined
                      ? ` · +${delta.toLocaleString()} since last`
                      : ""
                  }`
                : e.message;
            return (
              <div key={e.id} className="px-2.5 py-1.5">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-mono text-[11px] text-slate-500">
                    {fmtTime(e.ts)}
                  </span>
                  <span
                    className={`inline-flex shrink-0 items-center rounded border px-1.5 py-0 text-[10px] font-mono uppercase tracking-wide ${pill.color}`}
                  >
                    {pill.label}
                  </span>
                  <span className="rounded border border-slate-700 bg-slate-800 px-1.5 py-0 font-mono text-[10px] text-slate-300">
                    {e.actor}
                  </span>
                  {e.task_id ? (
                    <span className="font-mono text-[11px] text-pink-300">
                      {e.task_id}
                    </span>
                  ) : null}
                  <span
                    className={`min-w-0 flex-1 truncate text-[12px] ${
                      isTokenUsage ? "text-fuchsia-200" : "text-slate-200"
                    }`}
                    title={bodyText}
                  >
                    {bodyText}
                  </span>
                  {hasMetadata ? (
                    <button
                      type="button"
                      onClick={() => {
                        const next = new Set(expanded);
                        if (isExpanded) next.delete(e.id);
                        else next.add(e.id);
                        setExpanded(next);
                      }}
                      className="shrink-0 rounded border border-slate-700 px-1.5 py-0 text-[10px] text-slate-400 hover:border-slate-500 hover:text-white"
                      aria-expanded={isExpanded}
                    >
                      {isExpanded ? "▼ meta" : "▶ meta"}
                    </button>
                  ) : null}
                </div>
                {isExpanded && hasMetadata ? (
                  <pre className="mt-1 overflow-x-auto rounded border border-slate-800 bg-slate-950 p-2 text-[11px] text-slate-300">
                    {JSON.stringify(e.metadata, null, 2)}
                  </pre>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </AdminPageShell>
  );
}

// ────────────────────────────────────────────────────────────────────
// T-115 — live cluster-pulse table.
// Per-actor (frontend-worker / backend-worker / wire-test-worker /
// marc / merge-daemon) view of in-flight + queued tasks with status
// dot + age + counts, auto-refreshing every 5sec. Legacy actor names
// (chat-opus / frontend-worker / backend-worker) are filtered out
// client-side until the backend stops emitting them.
// Backend aggregator at GET /api/admin/cluster-pulse; payload is
// titles + IDs only (no handoff_text per chat-security-hygiene).
// ────────────────────────────────────────────────────────────────────

// Legacy actors retired by T-336 worker rename. The backend still
// emits some events under these names during the rollover; filter
// them out of the visible pulse so the UI shows canonical roles only.
const RETIRED_ACTORS = new Set([
  "chat-opus",
  "frontend-worker",
  "backend-worker",
  "frontend-worker",
  "backend-worker",
]);

type SlimTask = {
  id: string;
  title: string;
  status: string;
  blocked_by: string[];
  release_priority: string | null;
  touched_zones_count: number | null;
  started_at: string | null;
  created_at: string | null;
  last_progress_at: string | null;
};

type ActorPulse = {
  name: string;
  status_color: "green" | "amber" | "red" | "idle";
  last_activity_at: string | null;
  in_flight: SlimTask[];
  queued: SlimTask[];
  in_flight_count: number;
  queued_count: number;
};

type ClusterPulseOut = {
  generated_at: string;
  actors: ActorPulse[];
};

const PULSE_POLL_MS = 5_000;

function ClusterPulseSection() {
  const [pulse, setPulse] = useState<ClusterPulseOut | null>(null);
  const [pulseError, setPulseError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchOnce = () => {
      mcpFetch<ClusterPulseOut>("/admin/api/q-stream/cluster-pulse")
        .then((data) => {
          if (!cancelled) {
            setPulse(data);
            setPulseError(null);
          }
        })
        .catch((err) => {
          if (cancelled) return;
          setPulseError(
            err instanceof ApiError
              ? `Pulse fetch failed (HTTP ${err.status})`
              : "Pulse fetch failed",
          );
        });
    };
    fetchOnce();
    const id = setInterval(fetchOnce, PULSE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  return (
    <AdminCard>
      <div className="mb-3 flex items-center gap-2">
        <h2 className="text-sm font-semibold text-white">Cluster pulse</h2>
        <span className="text-[11px] text-slate-500">
          auto-refresh every {PULSE_POLL_MS / 1000}s
        </span>
        {pulse?.generated_at ? (
          <span className="ml-auto text-[11px] text-slate-500">
            generated {new Date(pulse.generated_at).toLocaleTimeString()}
          </span>
        ) : null}
      </div>
      {pulseError ? (
        <p className="text-xs text-rose-300">{pulseError}</p>
      ) : pulse === null ? (
        <p className="text-xs text-slate-500">Loading pulse…</p>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {pulse.actors
            .filter((a) => !RETIRED_ACTORS.has(a.name))
            .map((a) => (
              <ActorColumn key={a.name} actor={a} />
            ))}
        </div>
      )}
    </AdminCard>
  );
}

const STATUS_DOT: Record<ActorPulse["status_color"], string> = {
  green: "bg-emerald-400",
  amber: "bg-amber-400",
  red: "bg-rose-400",
  idle: "bg-slate-600",
};

function ActorColumn({ actor }: { actor: ActorPulse }) {
  return (
    <div className="rounded-md border border-slate-800 bg-slate-950/40 p-3">
      <div className="mb-2 flex items-center gap-2">
        <span
          className={`inline-block h-2.5 w-2.5 rounded-full ${STATUS_DOT[actor.status_color]}`}
          title={`status: ${actor.status_color}`}
        />
        <span className="font-mono text-xs font-semibold text-white">
          {actor.name}
        </span>
        <span className="ml-auto text-[10px] text-slate-500">
          {actor.in_flight_count} in-flight · {actor.queued_count} queued
        </span>
      </div>

      <div className="text-[11px] uppercase tracking-wide text-slate-500">
        In-flight
      </div>
      {actor.in_flight.length === 0 ? (
        <p className="text-xs text-slate-500">— none —</p>
      ) : (
        <ul className="space-y-1">
          {actor.in_flight.map((t) => (
            <TaskRow key={t.id} task={t} variant="in-flight" />
          ))}
        </ul>
      )}

      <div className="mt-3 text-[11px] uppercase tracking-wide text-slate-500">
        Queued
      </div>
      {actor.queued.length === 0 ? (
        <p className="text-xs text-slate-500">— none —</p>
      ) : (
        <ul className="space-y-1">
          {actor.queued.map((t) => (
            <TaskRow key={t.id} task={t} variant="queued" />
          ))}
        </ul>
      )}
    </div>
  );
}

function TaskRow({
  task,
  variant,
}: {
  task: SlimTask;
  variant: "in-flight" | "queued";
}) {
  const ageAnchor =
    variant === "in-flight"
      ? task.last_progress_at || task.started_at
      : task.created_at;
  const ageText = ageAnchor ? relTime(ageAnchor) : "?";
  const stale = variant === "in-flight" && isStale(ageAnchor);
  const blocked = task.blocked_by.length > 0;
  return (
    <li
      className={`rounded-md border ${stale ? "border-amber-700/40" : "border-slate-800/50"} bg-slate-900/40 px-2 py-1`}
    >
      <div className="flex flex-wrap items-baseline gap-1.5">
        <span className="font-mono text-[11px] font-semibold text-slate-200">
          {task.id}
        </span>
        {task.release_priority === "immediate" ? (
          <span className="rounded-md border border-rose-500/30 bg-rose-500/15 px-1 py-0.5 text-[10px] uppercase tracking-wide text-rose-300">
            immed
          </span>
        ) : null}
        {blocked ? (
          <span
            className="text-[10px] text-amber-300"
            title={`blocked by ${task.blocked_by.join(", ")}`}
          >
            🔒 {task.blocked_by.length}
          </span>
        ) : null}
        <span className="ml-auto text-[10px] text-slate-500">{ageText}</span>
      </div>
      <div className="text-[11px] text-slate-300 line-clamp-2" title={task.title}>
        {task.title}
      </div>
    </li>
  );
}

function relTime(iso: string): string {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diffSec = Math.max(0, Math.floor((now - then) / 1000));
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ${diffMin % 60}m ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay}d ago`;
  } catch {
    return "?";
  }
}

function isStale(iso: string | null): boolean {
  if (!iso) return false;
  try {
    const ageMin = (Date.now() - new Date(iso).getTime()) / 60000;
    return ageMin >= 60; // amber threshold per cardinal v2
  } catch {
    return false;
  }
}
